'use client';

import { useCallback, useRef } from 'react';

/**
 * Returns a memoized function that will only call the passed function when it hasn't been called for the msec period
 * @param func The function to be called
 * @param msec Wait period after function hasn't been called for
 * @returns A memoized function that is debounced
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDebounceCallback<T extends (...args: any[]) => any>(callback: T, msec: number = 500) {
  // Use a ref to store the timeout between renders
  // and prevent changes to it from causing re-renders
  const timeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  return useCallback((...args: unknown[]) => {
    const later = () => {
      clearTimeout(timeout.current);
      callback(...args);
    };

    clearTimeout(timeout.current);
    timeout.current = setTimeout(later, msec);
  }, [callback, msec]);

}


/*
import { DependencyList, useRef } from 'react';
import { useUnmountEffect } from '../useUnmountEffect/useUnmountEffect';

export interface DebouncedFunction<Fn extends (...args: any[]) => any> {
  (this: ThisParameterType<Fn>, ...args: Parameters<Fn>): void;
}
*/

/**
 * Makes passed function debounced, otherwise acts like `useCallback`.
 *
 * @param callback Function that will be debounced.
 * @param deps Dependencies list when to update callback.
 * @param delay Debounce delay.
 * @param maxWait The maximum time `callback` is allowed to be delayed before
 * it's invoked. 0 means no max wait.
 */
